import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ApprovePopup from "./ApprovePopup";
import TableLoader from "Components/Common/Loader/TableLoader";
import Pagination from "Components/Pagination/Pagination";
import { MdCancel } from "react-icons/md";
import { HiSearch } from "react-icons/hi";
import { usePermission } from "Context/PermissionContext";

const ApproveReq = ({ fetchStats }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [withdrawalItem, setWithdrawalItem] = useState([]);
  const [withdrawalRequest, setWithdrawalRequest] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [approvePopupOpen, setApprovePopupOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    from: null,
    to: null,
  });

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const rowsPerPage = 5;
  const { permissions } = usePermission();
  const currentTabPermissions =
    permissions?.payout?.subModule?.approve?.permissions;

  const handleOpenApprovePopup = (row) => {
    if (!currentTabPermissions.edit) {
      return;
    }
    setWithdrawalItem(row);
    setApprovePopupOpen(true);
  };

  const handleCloseApprovePopup = () => {
    setApprovePopupOpen(false);
  };

  // Fetch list + stats (one API call only)
  const getWithdrawalRequest = async (
    page = currentPage,
    query = searchTerm
  ) => {
    try {
      setLoading(true);

      const statusParam = activeTab === "All" ? "ALL" : activeTab.toLowerCase();

      const params = {
        paymentStatus: statusParam,
        page,
        limit: rowsPerPage,
        searchQuery: query.trim(),
      };

      // Apply date filter only when both exist
      if (dateFilter.from && dateFilter.to) {
        params.dateFilter = dateFilter;
      }

      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/tpi-payout/transactions/payment-status`,
        { params }
      );

      setWithdrawalRequest(data?.transactions || []);
      setTotalPages(data?.totalPages || 1);
      setTotalTransactions(data?.totalTransactions || 0);
    } catch (error) {
      console.log("Error fetching withdrawal request:", error);
    } finally {
      setLoading(false);
    }
  };

  // Effect for tab/page change
  // useEffect(() => {
  //   if (dateFilter.from && dateFilter.to) {
  //     // setCurrentPage(1);
  //     getWithdrawalRequest(1, searchTerm);
  //   }

  //   // If both cleared → fetch all
  //   if (!dateFilter.from || !dateFilter.to) {
  //     getWithdrawalRequest(searchTerm);
  //   }
  // }, [dateFilter.from, dateFilter.to, activeTab, currentPage]);

  useEffect(() => {
    // Do nothing if only one date is selected
    if (
      (dateFilter.from && !dateFilter.to) ||
      (!dateFilter.from && dateFilter.to)
    ) {
      return;
    }

    getWithdrawalRequest(currentPage, searchTerm);
  }, [currentPage, activeTab, dateFilter.from, dateFilter.to]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, dateFilter.from, dateFilter.to]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      getWithdrawalRequest(1, searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  return (
    <React.Fragment>
      <div className="py-5">
        {/* Search Box */}

        <div className="mx-auto my-1 w-auto rounded-t-2xl">
          {/* Tabs */}
          <div className="mb-6 rounded-xl bg-white p-4 shadow-md">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              {/* Tabs */}
              <div className="flex flex-wrap gap-2">
                {["All", "Pending", "Approved", "Rejected"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      setActiveTab(tab);
                      setCurrentPage(1);
                    }}
                    className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                      activeTab === tab
                        ? "bg-[#c51369] text-white shadow"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Date Filters */}
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="date"
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                  onChange={(e) =>
                    setDateFilter((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                />
                <input
                  type="date"
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                  onChange={(e) =>
                    setDateFilter((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                />
              </div>

              {/* Search */}
              <div className="relative w-full sm:w-72">
                <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by phone / UTR"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-9 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <MdCancel />
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <TableLoader />
          ) : (
            <div className="overflow-x-auto rounded-t-2xl">
              <table className="w-full border-collapse rounded-t-2xl text-left text-sm">
                <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
                  <tr className="divide-x divide-gray-300 text-center">
                    <th className="px-4 py-3">Sl</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Name</th>
                    {/* <th className="px-4 py-3">Phone</th> */}
                    <th className="px-4 py-3">UPI ID & UTR</th>
                    <th className="px-4 py-3">Settled Amount (₹)</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 text-center">
                  {withdrawalRequest.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-3 py-6 text-gray-500">
                        {searchTerm.trim()
                          ? "No records found matching your search"
                          : `No ${
                              activeTab === "All" ? "records" : activeTab
                            } data found`}
                      </td>
                    </tr>
                  ) : (
                    withdrawalRequest.map((row, index) => (
                      <tr
                        key={row._id}
                        className={`text-md divide-x divide-gray-200 bg-white tracking-wide text-gray-700 ${
                          row.paymentStatus === "pending" &&
                          activeTab === "Pending" &&
                          currentTabPermissions?.edit
                            ? "cursor-pointer hover:bg-pink-50"
                            : ""
                        }`}
                        onClick={() =>
                          activeTab === "Pending" &&
                          row.paymentStatus === "pending" &&
                          handleOpenApprovePopup(row)
                        }
                      >
                        <td className="px-3 py-4">
                          {index + 1 + (currentPage - 1) * rowsPerPage}
                        </td>
                        <td className="px-3 py-4">
                          <div className="flex  flex-col items-center space-x-2">
                            <span>
                              {new Date(row.updatedAt).toLocaleDateString()}
                            </span>
                            <span>
                              {new Date(row.updatedAt).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              })}
                            </span>
                          </div>
                        </td>

                        <td className="px-3 py-4">
                          <div className="flex flex-col">
                            <span className="font-medium">{row?.name}</span>
                            <span className="font-medium text-gray-400">
                              Nick name - {row?.userId?.nickName || "--"}
                            </span>
                            <td className="font-medium text-gray-400">
                              Phone - {row?.userId?.phone?.slice(2) || "-"}
                            </td>
                          </div>
                        </td>

                        <td className="px-3 py-4">
                          <div className="flex flex-col gap-2 text-center text-sm">
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {row?.upiId || "-"}
                              </span>
                              <span className="font-medium text-gray-400">
                                {row?.payid}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col gap-2 text-sm">
                            <span>Requested: ₹{row?.amount?.toFixed(2)}</span>
                            <span>
                              Charges: ₹{row?.charges?.toFixed(2) || 0}{" "}
                              <span>(5%)</span>
                            </span>
                            <span>
                              Disbursed: ₹
                              {row?.disbursedAmount?.toFixed(2) || 0}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-4">
                          <div
                            className={`rounded-lg p-2 text-xs ${
                              row?.paymentStatus === "pending"
                                ? "bg-orange-100 text-orange-500"
                                : row?.paymentStatus === "rejected"
                                ? "bg-red-100 text-red-500"
                                : "bg-green-100 text-green-500"
                            }`}
                          >
                            {row?.paymentStatus.toUpperCase()}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Approve Popup (keeps fetchStats for instant refresh on approve/reject) */}
          {approvePopupOpen && (
            <ApprovePopup
              key={withdrawalItem?._id}
              approvePopupOpen={approvePopupOpen}
              handleCloseApprovePopup={handleCloseApprovePopup}
              withdrawalItem={withdrawalItem}
              getWithdrawalRequest={getWithdrawalRequest}
              fetchStats={fetchStats}
            />
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default ApproveReq;
